<template>
    <div v-if="isReady" :class="{ 'partner-portal-admin': $store.getters.isAdmin }">
        <div class="p-ml-auto p-mr-auto mf-menu-container">
            <Toast position="top-right" :baseZIndex="10000" />
            <Menubar :model="items" v-if="!$route.meta.hideMenu">
                <template #start>
                    <div class="app__logo-container" @click="routeHome">
                        <img src="./assets/logo_white.svg" class="logo" />
                    </div>
                </template>
                <template #end>
                    <div class="app__menu-container">
                        <Button
                            :label="$store.getters.usernameOrEmail"
                            icon="pi pi-angle-down"
                            iconPos="right"
                            class="p-button-text p-button-dropdown"
                            @click="toggle"
                        />
                        <Menu
                            id="partner-portal-menu"
                            :model="secondaryItems"
                            :popup="true"
                            ref="menu"
                        >
                            <template #item="{ item }">
                                <div v-if="item.PartnerImpersonation">
                                    <PartnerImpersonation />
                                </div>
                                <router-link
                                    v-else-if="item.to && !menu.disabled(item)"
                                    :to="item.to"
                                    custom
                                    v-slot="{ href, navigate, isActive, isExactActive }"
                                >
                                    <a
                                        class="p-menuitem-link"
                                        :class="{
                                            'active-link': isActive,
                                            'active-link-exact': isExactActive
                                        }"
                                        :href="href"
                                        @click="navigate"
                                    >
                                        <span
                                            v-if="item.icon"
                                            :class="['p-menuitem-icon', item.icon]"
                                        />
                                        <span class="p-menuitem-text">
                                            {{ item.label }}
                                        </span>
                                    </a>
                                </router-link>
                                <a
                                    v-else
                                    :href="item.url"
                                    class="p-menuitem-link"
                                    :target="item.target"
                                    role="menuitem"
                                >
                                    <span
                                        v-if="item.icon"
                                        :class="['p-menuitem-icon', item.icon]"
                                    />

                                    <span class="p-menuitem-text">
                                        {{ item.label }}
                                    </span>
                                </a>
                            </template>
                        </Menu>
                    </div>
                </template>
            </Menubar>
        </div>

        <div class="p-ml-auto p-mr-auto mf-router-container">
            <div class="p-ml-6 p-mr-6">
                <PageTitle />
                <router-view />
            </div>
        </div>
    </div>

    <Footer />

    <ConfirmDialog />
</template>

<script>
import Footer from '@/components/Footer.vue';
import PageTitle from '@/components/PageTitle.vue';
import PartnerImpersonation from '@/components/PartnerImpersonation.vue';
import Toast from 'primevue/toast';
import Menubar from 'primevue/menubar';
import Menu from 'primevue/menu';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { defineComponent, inject, ref, watch, onMounted, computed } from 'vue';
import { setVariable } from '@/plugins/mouseflow';
import { useI18n } from 'vue-i18n';

export default defineComponent({
    name: 'App',
    components: {
        Toast,
        Menubar,
        Footer,
        PageTitle,
        PartnerImpersonation,
        Menu,
        ConfirmDialog
    },
    setup() {
        const constants = inject('Constants');
        const confirm = useConfirm();
        const { t } = useI18n();
        initIntercom(constants.INTERCOM_APP_ID);

        const store = useStore();
        const router = useRouter();
        const menu = ref();
        const isReady = ref(false);

        store
            .dispatch('autoSignIn')
            .then(() => {
                if (!store.getters.isAdmin)
                    window.Intercom('boot', {
                        app_id: constants.INTERCOM_APP_ID,
                        email: store.state.user.email,
                        name: store.state.user.name,
                        created_at: Date.parse(store.state.user.created) / 1000
                    });
            })
            .catch(() => {})
            .finally(() => {
                isReady.value = true;
            });

        router.beforeEach((to, from, next) => {
            if (store.getters.isSignedIn || to.meta.allowAnonymous) next();
            else next({ name: 'SignIn' });
        });

        const items = computed(() => getMenuItems(store, t));

        const signOut = () => {
            store.dispatch('signOut').then(() => {
                window.Intercom('shutdown');
                router.push({ name: 'SignIn' });
            });
        };
        const manageRelations = () =>
            confirm.require({
                message: t('admin.manageRelation.warn'),
                header: t('general.confirmProceed'),
                accept: () =>
                    router.push({
                        name: 'ManageRelations'
                    }),
                reject: () => {}
            });
        const secondaryItems = getSecondaryMenuItems(store, signOut, manageRelations, t);

        const toggle = (event) => {
            menu.value.toggle(event);
        };

        const routeHome = () => {
            if (store.getters.isAdmin) router.push({ name: 'Partners' });
            else router.push({ name: 'Dashboard' });
        };

        watch(
            () => store.state.user,
            (user) => {
                if (user) {
                    setVariable('partner-id', user.partnerId);
                    setVariable('is-admin', store.getters.isAdmin);
                }
            }
        );

        onMounted(() => {
            document.title = t('general.appTitle');
        });

        return {
            items,
            secondaryItems,
            menu,
            signOut,
            toggle,
            routeHome,
            manageRelations,
            isReady
        };
    }
});

function getMenuItems(store, t) {
    return [
        {
            label: t('menu.partner.clients'),
            to: { name: 'Dashboard' },
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menu.partner.connect'),
            to: { name: 'InviteClient' },
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menu.partner.team'),
            to: { name: 'Consultants' },
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menu.partner.transactions'),
            to: { name: 'PartnerTransactions' },
            visible: () => !store.getters.isAdmin
        },
        // {
        //    label: 'Sales',
        //    url: 'https://www.mouseflow.com/',
        //     visible: () => !store.getters.isAdmin
        // },
        {
            label: t('menu.partner.partnerResources'),
            url: 'https://go.mouseflow.com/partner-resources',
            target: '_blank',
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menu.admin.partners'),
            to: { name: 'Partners' },
            visible: () => store.getters.isAdmin
        },
        {
            label: t('menu.admin.invite'),
            to: { name: 'InvitePartner' },
            visible: () => store.getters.isAdmin
        },
        {
            label: t('menu.admin.applications'),
            to: { name: 'Applications' },
            visible: () => store.getters.isAdmin
        },
        {
            label: t('menu.admin.users'),
            to: { name: 'UsersList' },
            visible: () => store.getters.isAdmin
        },
        {
            label: t('menu.admin.transactions'),
            to: { name: 'Transactions' },
            visible: () => store.getters.isAdmin
        },
        {
            label: t('menu.admin.commissions'),
            to: { name: 'Commissions' },
            visible: () => store.getters.isAdmin
        }
    ];
}

function impersonatePartner(store, t) {
    var email = prompt(t('general.impersonatePrompt'));
    store.dispatch('admin/impersonatePartner', email);
}

function getSecondaryMenuItems(store, signOut, manageRelations, t) {
    return [
        {
            PartnerImpersonation: true,
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menuSecondary.partner.partnerSettings'),
            icon: 'pi pi-cog',
            to: { name: 'PartnerSettings' },
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menuSecondary.partner.mySettings'),
            icon: 'pi pi-user',
            to: { name: 'MySettings' },
            visible: () => !store.getters.isAdmin
        },
        {
            label: t('menuSecondary.admin.impersonate'),
            icon: 'pi pi-users',
            visible: () => store.getters.isAdmin,
            command: () => {
                impersonatePartner(store, t);
            }
        },
        {
            label: t('menuSecondary.admin.manualConnections'),
            icon: 'pi pi-exclamation-triangle',
            visible: () => store.getters.isAdmin,
            command: () => {
                manageRelations();
            }
        },
        {
            label: t('menuSecondary.common.signOut'),
            icon: 'pi pi-sign-out',
            visible: true,
            command: () => {
                signOut();
            }
        }
    ];
}

function initIntercom(appId) {
    var w = window;
    var ic = w.Intercom;
    if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
    } else {
        var d = document;
        var i = function () {
            i.c(arguments);
        };
        i.q = [];
        i.c = function (args) {
            i.q.push(args);
        };
        w.Intercom = i;
        var l = function () {
            var s = d.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.src = 'https://widget.intercom.io/widget/' + appId;
            var x = d.getElementsByTagName('script')[0];
            x.parentNode.insertBefore(s, x);
        };
        if (w.attachEvent) {
            w.attachEvent('onload', l);
        } else {
            w.addEventListener('load', l, false);
        }
    }
}
</script>

<style lang="scss">
.app {
    &__logo-container {
        display: flex;
        align-items: center;
        height: 40px;
        margin-left: 54px;
        margin-right: 32px;
        cursor: pointer;

        .logo {
            margin-right: 10px;
            height: 28px;
        }
    }

    &__menu-container {
        padding-right: 24px;
    }
}
</style>
